import React, { useState, useEffect, useRef } from 'react';
import { Users, Plus, Trash2, Loader2, Mail, Phone, UserCheck, UserX, X, Search, Camera, MoreHorizontal } from 'lucide-react';
import { getTeachers, createTeacher, updateTeacher, deleteTeacher, uploadTeacherPhoto } from '@/actions/data-fetch-teachers';
import type { Teacher } from '@/actions/data-fetch-teachers';
import { toastMessage } from '@/utils/toast-message';

const AV_COLORS = ['av-blue', 'av-purple', 'av-green', 'av-teal', 'av-rose', 'av-indigo', 'av-peach', 'av-amber'];

function avatarColor(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffff;
  return AV_COLORS[h % AV_COLORS.length];
}

function initials(name: string) {
  return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

// ─── Drawer ───────────────────────────────────────────────────────────────────
const TeacherDrawer: React.FC<{
  teacher?: Teacher | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ teacher, onClose, onSaved }) => {
  const [name,    setName]    = useState(teacher?.name  ?? '');
  const [email,   setEmail]   = useState(teacher?.email ?? '');
  const [phone,   setPhone]   = useState(teacher?.phone ?? '');
  const [submitting,     setSubmitting]     = useState(false);
  const [photoPreview,   setPhotoPreview]   = useState<string | null>(teacher?.photo_url ?? null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const isEdit  = !!teacher;

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !teacher) return;
    setPhotoPreview(URL.createObjectURL(file));
    setUploadingPhoto(true);
    try {
      const res = await uploadTeacherPhoto(teacher.id, file);
      if (res?.success) toastMessage('Photo updated', 'success');
      else toastMessage(res?.message || 'Upload failed', 'error');
    } finally { setUploadingPhoto(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || (!isEdit && !email.trim())) {
      toastMessage('Name and email are required', 'error'); return;
    }
    if (!/^[a-zA-Z\s.'-]+$/.test(name.trim())) {
      toastMessage('Name should contain only letters', 'error'); return;
    }
    if (!isEdit && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toastMessage('Please enter a valid email address', 'error'); return;
    }
    if (phone.trim() && !/^\+?[0-9\s\-()]{7,15}$/.test(phone.trim())) {
      toastMessage('Phone number should contain only numbers', 'error'); return;
    }
    setSubmitting(true);
    try {
      const res = isEdit
        ? await updateTeacher(teacher.id, { name: name.trim(), phone: phone.trim() || undefined })
        : await createTeacher({ name: name.trim(), email: email.trim(), phone: phone.trim() || undefined });
      if (res.success) {
        toastMessage(isEdit ? 'Teacher updated' : 'Teacher added', 'success');
        onSaved();
      } else {
        toastMessage((res as any).message || 'Failed', 'error');
      }
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="drawer">
        <div className="drawer-head">
          <div style={{ flex: 1 }}>
            <h2>{isEdit ? 'Edit Teacher' : 'Add Teacher'}</h2>
            <p>{isEdit ? 'Update staff details' : 'Invite a new member of teaching staff'}</p>
          </div>
          <button className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="drawer-body">
          <form id="teacher-form" onSubmit={handleSubmit}>
            {/* Photo — edit mode only */}
            {isEdit && (
              <div className="row gap-16" style={{ marginBottom: 24 }}>
                <div
                  style={{ position: 'relative', cursor: 'pointer', flexShrink: 0 }}
                  onClick={() => fileRef.current?.click()}
                  onMouseEnter={e => { const ov = (e.currentTarget as HTMLElement).querySelector('.ph-ov') as HTMLElement | null; if (ov) ov.style.opacity = '1'; }}
                  onMouseLeave={e => { const ov = (e.currentTarget as HTMLElement).querySelector('.ph-ov') as HTMLElement | null; if (ov) ov.style.opacity = '0'; }}
                >
                  {photoPreview ? (
                    <img src={photoPreview} alt="" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <div className={`avatar ${avatarColor(teacher?.name || '')}`} style={{ width: 72, height: 72, fontSize: 22 }}>
                      {initials(teacher?.name || '?')}
                    </div>
                  )}
                  <div className="ph-ov center" style={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    background: 'rgba(0,0,0,0.38)', opacity: 0, transition: 'opacity .12s',
                  }}>
                    {uploadingPhoto
                      ? <Loader2 size={18} color="#fff" style={{ animation: 'pa-spin .7s linear infinite' }} />
                      : <Camera size={18} color="#fff" />}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Profile photo</div>
                  <div className="meta" style={{ marginTop: 2, marginBottom: 8 }}>Optional · JPG or PNG</div>
                  <button type="button" className="btn btn-soft btn-sm" onClick={() => fileRef.current?.click()}>Upload</button>
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
              </div>
            )}

            <div className="col gap-16">
              <div className="field">
                <label>Full name <span className="req">*</span></label>
                <input autoFocus className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Meena Krishnan" />
              </div>
              <div className="field">
                <label>
                  Email <span className="req">*</span>
                  {isEdit && <span className="opt"> · cannot be changed</span>}
                </label>
                <input
                  type="email" className="input" value={email}
                  onChange={e => !isEdit && setEmail(e.target.value)}
                  disabled={isEdit} placeholder="teacher@school.edu"
                  style={isEdit ? { opacity: 0.55, cursor: 'not-allowed' } : {}}
                />
              </div>
              <div className="field">
                <label>Phone <span className="opt">optional</span></label>
                <input type="tel" className="input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 98765 43210" />
              </div>
            </div>
          </form>
        </div>

        <div className="drawer-foot">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            form="teacher-form" type="submit" className="btn btn-primary"
            disabled={submitting || !name.trim() || (!isEdit && !email.trim())}
          >
            {submitting && <Loader2 size={14} style={{ animation: 'pa-spin .7s linear infinite' }} />}
            {isEdit ? 'Save changes' : 'Add teacher'}
          </button>
        </div>
      </div>
    </>
  );
};

// ─── Row action menu ──────────────────────────────────────────────────────────
const RowMenu: React.FC<{
  teacher: Teacher;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}> = ({ teacher, onEdit, onToggle, onDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button className="icon-btn" onClick={() => setOpen(o => !o)}>
        <MoreHorizontal size={17} />
      </button>
      {open && (
        <div className="card" style={{
          position: 'absolute', right: 0, top: 38, width: 170,
          padding: 6, zIndex: 40, boxShadow: 'var(--sh-lg)',
        }}>
          <button className="nav-item" style={{ borderRadius: 9, padding: '8px 10px', color: 'var(--text)', gap: 9 }}
            onClick={() => { setOpen(false); onEdit(); }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Edit details</span>
          </button>
          <button className="nav-item" style={{ borderRadius: 9, padding: '8px 10px', color: 'var(--text)', gap: 9 }}
            onClick={() => { setOpen(false); onToggle(); }}>
            {teacher.is_active
              ? <UserX size={15} />
              : <UserCheck size={15} />}
            <span style={{ fontSize: 13, fontWeight: 600 }}>{teacher.is_active ? 'Deactivate' : 'Activate'}</span>
          </button>
          <div style={{ borderTop: '1px solid var(--divider)', margin: '4px 0' }} />
          <button className="nav-item" style={{ borderRadius: 9, padding: '8px 10px', color: 'var(--danger)', gap: 9 }}
            onClick={() => { setOpen(false); onDelete(); }}>
            <Trash2 size={15} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>Remove</span>
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const TeachersManagement: React.FC = () => {
  const [teachers,       setTeachers]       = useState<Teacher[]>([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState('');
  const [drawerMode,     setDrawerMode]     = useState<'add' | 'edit' | null>(null);
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getTeachers();
      if (res.success) setTeachers((res.data as any)?.rows ?? res.data ?? []);
      else toastMessage((res as any).message || 'Failed to load teachers', 'error');
    } finally { setLoading(false); }
  };

  const closeDrawer = () => {
    setDrawerMode(null);
    setEditingTeacher(null);
  };

  const handleSaved = async () => {
    closeDrawer();
    await load();
  };

  const handleToggleActive = async (teacher: Teacher) => {
    const res = await updateTeacher(teacher.id, { is_active: !teacher.is_active });
    if (res.success) {
      toastMessage(teacher.is_active ? 'Teacher deactivated' : 'Teacher activated', 'success');
      await load();
    }
  };

  const handleDelete = async (teacher: Teacher) => {
    if (!confirm(`Remove ${teacher.name}? This cannot be undone.`)) return;
    const res = await deleteTeacher(teacher.id);
    if (res.success) {
      toastMessage('Teacher removed', 'success');
      await load();
    }
  };

  const filtered = teachers.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.email.toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = teachers.filter(t => t.is_active).length;

  return (
    <>
      <style>{`@keyframes pa-spin { to { transform: rotate(360deg); } }`}</style>

      {/* Page header */}
      <div className="page-head">
        <div className="page-head-icon">
          <Users size={20} strokeWidth={2} />
        </div>
        <div className="page-head-text">
          <h1>Teachers</h1>
          <p>{loading ? 'Loading…' : `${activeCount} active · ${teachers.length} total`}</p>
        </div>
        <div className="page-head-actions">
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
            <input
              type="text" className="input" value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              style={{ paddingLeft: 36, width: 260 }}
            />
          </div>
          <button className="btn btn-primary" onClick={() => setDrawerMode('add')}>
            <Plus size={15} strokeWidth={2.5} />
            Add Teacher
          </button>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        {loading ? (
          <div className="center" style={{ paddingTop: 80 }}>
            <Loader2 size={24} style={{ animation: 'pa-spin .7s linear infinite', color: 'var(--text-3)' }} />
          </div>
        ) : teachers.length === 0 ? (
          <div className="card">
            <div className="empty">
              <div className="empty-art"><Users size={36} /></div>
              <h3>No teachers yet</h3>
              <p>Add your teaching staff to assign them to classes and subjects. They'll receive an invite to join.</p>
              <button className="btn btn-primary" onClick={() => setDrawerMode('add')}>
                <Plus size={15} />
                Add your first teacher
              </button>
            </div>
          </div>
        ) : (
          <>
            {filtered.length === 0 ? (
              <div className="card">
                <div className="empty" style={{ padding: '40px 24px' }}>
                  <h3 style={{ fontSize: 15 }}>No results</h3>
                  <p>No teachers match "{search}"</p>
                </div>
              </div>
            ) : (
              <div style={{ overflow: 'auto', maxHeight: '520px' }}>
                <table className="tbl tbl-sticky">
                  <thead>
                    <tr>
                      <th style={{ width: '35%' }}>Teacher</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th style={{ width: 52 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(teacher => (
                      <tr key={teacher.id} style={!teacher.is_active ? { opacity: 0.55 } : {}}>
                        <td>
                          <div className="row gap-12">
                            {teacher.photo_url ? (
                              <img src={teacher.photo_url} alt={teacher.name}
                                style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                            ) : (
                              <div className={`avatar ${avatarColor(teacher.name)}`} style={{ width: 40, height: 40, fontSize: 13 }}>
                                {initials(teacher.name)}
                              </div>
                            )}
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 13.5, letterSpacing: '-0.2px' }}>{teacher.name}</div>
                              <div className="meta row gap-4" style={{ marginTop: 2 }}>
                                <Mail size={11} />{teacher.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {teacher.phone ? (
                            <div className="meta row gap-4">
                              <Phone size={11} />{teacher.phone}
                            </div>
                          ) : (
                            <span style={{ color: 'var(--text-3)', fontSize: 13 }}>—</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${teacher.is_active ? 'b-success' : 'b-neutral'}`}>
                            <span className="dot" />
                            {teacher.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <RowMenu
                            teacher={teacher}
                            onEdit={() => { setEditingTeacher(teacher); setDrawerMode('edit'); }}
                            onToggle={() => handleToggleActive(teacher)}
                            onDelete={() => handleDelete(teacher)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Drawer */}
      {drawerMode !== null && (
        <TeacherDrawer
          teacher={drawerMode === 'edit' ? editingTeacher : null}
          onClose={closeDrawer}
          onSaved={handleSaved}
        />
      )}
    </>
  );
};

export default TeachersManagement;
